/* eslint-disable react/jsx-props-no-spreading */
import { useState, useEffect, type FC, useRef } from 'react';
import debounce from 'lodash.debounce';
import Icon, { type IconString } from '2-core-elements/Icon/Icon';
import PageView from './PageView';
import BreakView from './BreakView';
import { type ClickOrPressEvent } from 'types/mouseEvents';
import { BREAKPOINT_SM } from 'utils/breakpoint';


export const PAGINATION_DISABLED_CLASSNAME = 'pagination-disabled';
export const PAGINATION_SELECTED_CLASSNAME = 'pagination-selected';
export const PAGINATION_MOBILE_VIEW_CLASSNAME = 'pagination-mobile-view';

const handleClickOrPress = (e: ClickOrPressEvent): void => {
    e.preventDefault();
    e.stopPropagation();
    if (e.nativeEvent) {
        e.nativeEvent.stopImmediatePropagation();
    }
};

const DEBOUNCE_VALUE = 100;

interface PaginationPageInnerNumberProps {
    pageNum: number; // total number of pages
    // pageRangeDisplayed: number; // how many items beside the active page until break view minimum set to 2 to work properly
    // marginPagesDisplayed: number; // how many items after break view
    selected: number;
    onPageViewClick?: (selectedPage: number) => void;
    queryParamPage?: string;
    viewAllMaxItem: number;
    itemsBeforeBreakView: number;
    rangeSelectedItemToDisplay?: number;
    serverPageUrl?: string;
}

const PaginationPageInnerNumber: FC<PaginationPageInnerNumberProps> = ({
    pageNum,
    selected,
    onPageViewClick,
    queryParamPage,
    viewAllMaxItem,
    itemsBeforeBreakView,
    rangeSelectedItemToDisplay = 1,
    serverPageUrl,
}) => {
    // const MAX_ITEM = 7; // desktop including the break view
    const itemsArray: JSX.Element[] = [];

    if (pageNum <= viewAllMaxItem) {
        for (let i = 1; i < pageNum - 1; i += 1) {
            itemsArray.push(
                <PageView
                    key={`key${i}`}
                    onClick={(e: ClickOrPressEvent): void => {
                        handleClickOrPress(e);
                        if (onPageViewClick) {
                            onPageViewClick(i);
                        }
                    }}
                    selected={selected === i}
                    // className={pageClassName}
                    page={i + 1}
                    queryParamPage={queryParamPage}
                    serverPageUrl={serverPageUrl}
                />
            );
        }

        // eslint-disable-next-line react/jsx-no-useless-fragment
        return <>{itemsArray}</>;
    }

    for (let i = 1; i < pageNum - 1; i += 1) {
        // 1. check for left and right of selected number, 2. check for selected on the first 4 items, 3. check for selected on the last 4 items
        if ((i >= selected - rangeSelectedItemToDisplay && i <= selected + rangeSelectedItemToDisplay) ||
            (selected < itemsBeforeBreakView && i < itemsBeforeBreakView) ||
            (selected > pageNum - (itemsBeforeBreakView + 1) && i > pageNum - (itemsBeforeBreakView + 1))) {
            itemsArray.push(
                <PageView
                    key={`key${i}`}
                    onClick={(e: ClickOrPressEvent): void => {
                        handleClickOrPress(e);
                        if (onPageViewClick) {
                            onPageViewClick(i);
                        }
                    }}
                    selected={selected === i}
                    // className={pageClassName}
                    page={i + 1}
                    queryParamPage={queryParamPage}
                    serverPageUrl={serverPageUrl}
                />
            );
        }
    }

    // eslint-disable-next-line react/jsx-no-useless-fragment
    return <>{itemsArray}</>;
};

interface PaginationViewProps extends PaginationPageInnerNumberProps {
    breakLabel?: string | React.ReactNode;
}

const PaginationView: FC<PaginationViewProps> = ({
    selected,
    onPageViewClick,
    queryParamPage,
    breakLabel,
    pageNum,
    itemsBeforeBreakView,
    viewAllMaxItem,
    rangeSelectedItemToDisplay,
    serverPageUrl,
}) => {
    return (
        <>
            <PageView
                onClick={(e: ClickOrPressEvent): void => {
                    handleClickOrPress(e);
                    if (onPageViewClick) {
                        onPageViewClick(0);
                    }
                }}
                selected={selected === 0}
                page={1}
                queryParamPage={queryParamPage}
                serverPageUrl={serverPageUrl}
            />
            {selected >= itemsBeforeBreakView && pageNum > viewAllMaxItem && (
                <BreakView
                    breakLabel={breakLabel}
                />
            )}
            <PaginationPageInnerNumber
                pageNum={pageNum}
                selected={selected}
                onPageViewClick={onPageViewClick}
                queryParamPage={queryParamPage}
                viewAllMaxItem={viewAllMaxItem}
                itemsBeforeBreakView={itemsBeforeBreakView}
                rangeSelectedItemToDisplay={rangeSelectedItemToDisplay}
                serverPageUrl={serverPageUrl}
            />
            {selected < pageNum - itemsBeforeBreakView && pageNum > viewAllMaxItem && (
                <BreakView
                    breakLabel={breakLabel}
                />
            )}
            <PageView
                onClick={(e: ClickOrPressEvent): void => {
                    handleClickOrPress(e);
                    if (onPageViewClick) {
                        onPageViewClick(pageNum - 1);
                    }
                }}
                selected={selected === pageNum - 1}
                page={pageNum}
                queryParamPage={queryParamPage}
                serverPageUrl={serverPageUrl}
            />
        </>
    );
};

const SIZE_SETTINGS = {
    large: {
        viewAllMaxItem: 7,
        itemsBeforeBreakView: 4,
        rangeSelectedItemToDisplay: 1,
    },
    small: {
        viewAllMaxItem: 5,
        itemsBeforeBreakView: 2,
        rangeSelectedItemToDisplay: 0,
    },
};

interface PaginationProps extends Omit<PaginationViewProps, 'onPageViewClick' | 'selected' | 'viewAllMaxItem' | 'itemsBeforeBreakView' | 'rangeSelectedItemToDisplay'> {
    selectedPage?: number; // index of active page (by default pagination has its own state, this param force the value)
    className?: string;
    prevIcon?: IconString;
    nextIcon?: IconString;
    onChange?: (selectedPage: number) => void;
    serverPageUrl?: string;
}

const Pagination: FC<PaginationProps> = (props: PaginationProps) => {
    const {
        pageNum,
        // pageRangeDisplayed,
        // marginPagesDisplayed,
        selectedPage = 0,
        // initialSelectedPage = null,
        className = '',
        prevIcon = 'lg-chevron-left',
        nextIcon = 'lg-chevron-right',
        breakLabel = <Icon icon="more-horiz" />,
        onChange,
        queryParamPage = 'page',
        serverPageUrl,
    } = props;

    const [selected, setSelected] = useState(selectedPage);
    const [isLarge, setIsLarge] = useState(true);
    const isLargeRef = useRef(true);

    const onPageChange = (page: number): void => {
        if (
            onChange
            && typeof onChange === 'function'
        ) {
            onChange(page);
        }
    };

    useEffect(() => {
        if (selected !== selectedPage) {
            setSelected(selectedPage);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedPage]);

    const handlePageSelected = (newPage: number): void => {
        if (selected === newPage) {
            return;
        }

        setSelected(newPage);
        onPageChange(newPage);
    };

    const handlePreviousPage = (e: ClickOrPressEvent): void => {
        handleClickOrPress(e);
        if (selected > 0) {
            handlePageSelected(selected - 1);
        }
    };

    const handleNextPage = (e: ClickOrPressEvent): void => {
        handleClickOrPress(e);
        if (selected < pageNum - 1) {
            handlePageSelected(selected + 1);
        }
    };

    const previousClasses = selected === 0 ? PAGINATION_DISABLED_CLASSNAME : '';
    const nextClasses = selected === pageNum - 1 ? PAGINATION_DISABLED_CLASSNAME : '';

    const prevUrl = (typeof window !== 'undefined') ? `${window.location.href.split('?')[0]}?${queryParamPage}=${selected}` : `${serverPageUrl?.split('?')[0]}?${queryParamPage}=${selected}`;
    const nextUrl = (typeof window !== 'undefined') ? `${window.location.href.split('?')[0]}?${queryParamPage}=${selected + 2}` : `${serverPageUrl?.split('?')[0]}?${queryParamPage}=${selected + 2}`;

    const windowResizeCheck = (): void => {
        if (window.innerWidth >= BREAKPOINT_SM && !isLargeRef.current) {
            isLargeRef.current = true;
            setIsLarge(true);
        } else if (window.innerWidth < BREAKPOINT_SM && isLargeRef.current) {
            isLargeRef.current = false;
            setIsLarge(false);
        }
    };

    useEffect(() => {
        windowResizeCheck();
        window.addEventListener('resize', debounce(windowResizeCheck, DEBOUNCE_VALUE));

        return () => {
            window.removeEventListener('resize', debounce(windowResizeCheck, DEBOUNCE_VALUE));
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const sizeSettings = isLarge ? SIZE_SETTINGS.large : SIZE_SETTINGS.small;

    return (
        <nav className={`pagination ${className}`}>
            <div className="pagination-item-wrapper">
                <div className={`pagination-item pagination-item-prev ${previousClasses}`}>
                    <a
                        href={prevUrl}
                        onClick={handlePreviousPage}
                        className="pagination-item-content"
                        {...(selected === 0 ? { ...{ tabIndex: -1 } } : { ...{} })}
                    >
                        <Icon icon={prevIcon} />
                        <span className="sr-only">Previous</span>
                    </a>
                </div>

                <PaginationView
                    pageNum={pageNum}
                    selected={selected}
                    onPageViewClick={handlePageSelected}
                    queryParamPage={queryParamPage}
                    breakLabel={breakLabel}
                    {...sizeSettings}
                    serverPageUrl={serverPageUrl}
                />

                <div className={`pagination-item pagination-item-next ${nextClasses}`}>
                    <a
                        href={nextUrl}
                        onClick={handleNextPage}
                        className="pagination-item-content"
                        {...(selected === pageNum - 1 ? { ...{ tabIndex: -1 } } : { ...{} })}
                    >
                        <Icon icon={nextIcon} />
                        <span className="sr-only">Next</span>
                    </a>
                </div>
            </div>
        </nav>
    );
};

export default Pagination;
